import { parseMoneyToCents, safeLink, tt } from '@agos/shared'

import { contentList, contentText, type HomeModule } from './modules'

/**
 * What each homepage module draws, read out of its free-form `content` blob.
 *
 * The blobs are admin-authored and every field is optional, so each reader answers a default rather
 * than rendering `undefined`. Measured on the restored data, in `sortOrder`:
 *
 * ```
 * banner                addressText desktopImageUrl hoursText mobileImageUrl phoneText
 * social_links          googleRating subtitle title
 * personalized_greeting (empty)
 * most_ordered          (empty)
 * popular_dishes        productIds title
 * chef_recommendations  productIds title
 * guarantees            block1Title/Text block2Title/Text block3Title/Text
 * virtual_tour          heightDesktop heightMobile iframeUrl subtitle title
 * about_us              description imageUrl linkUrl
 * newsletter            ctaText placeholder subtitle title
 * testimonials          items subtitle title          (switched off)
 * promo_banner          25 fields                     (switched off)
 * ```
 */

// ─── the virtual tour, which is an admin-supplied URL in a frame ─────────────

/**
 * The providers a tour may come from.
 *
 * Legacy's list, unchanged and for its stated reason — *"an allow-list of provider hosts so we don't
 * render arbitrary third-party iframes."* Without it, whoever administers the restaurant can point a
 * frame on the customer's homepage at anything at all, which is a phishing surface handed out with the
 * CMS password.
 *
 * The configured URL today is a Google Maps embed.
 */
const ALLOWED_TOUR_HOSTS = [
  'my.matterport.com',
  'matterport.com',
  'www.google.com',
  'maps.google.com',
  'kuula.co',
  'www.kuula.co',
  'youtube.com',
  'www.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'cloudpano.com',
  'app.cloudpano.com',
  'panoraven.com',
  'www.panoraven.com',
  '360.virtualtourcafe.com',
  'vtoursoft.com',
  'panowideapp.com',
] as const

/**
 * The tour URL if it may be framed, otherwise `null`.
 *
 * HTTPS only, and the host must be on the list or a subdomain of one — `host === h || host.endsWith('.'
 * + h)`. The dot matters: without it `evilmatterport.com` would pass as a suffix of `matterport.com`.
 */
export function allowedTourUrl(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null
  let url: URL
  try {
    url = new URL(raw.trim())
  } catch {
    return null
  }
  if (url.protocol !== 'https:') return null
  const host = url.hostname.toLowerCase()
  const allowed = ALLOWED_TOUR_HOSTS.some((entry) => host === entry || host.endsWith(`.${entry}`))
  return allowed ? url.toString() : null
}

export interface VirtualTour {
  title: string
  subtitle: string
  url: string | null
  heightDesktop: number
  heightMobile: number
}

export function virtualTourOf(module: HomeModule): VirtualTour {
  return {
    title: contentText(module, 'title') ?? tt('Virtuálna prehliadka'),
    subtitle:
      contentText(module, 'subtitle') ?? tt('Pozrite sa do našej reštaurácie ešte pred návštevou.'),
    url: allowedTourUrl(module.content.iframeUrl),
    heightDesktop: positiveNumber(module.content.heightDesktop, 540),
    heightMobile: positiveNumber(module.content.heightMobile, 360),
  }
}

function positiveNumber(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

// ─── the two product carousels ───────────────────────────────────────────────

export interface PickedProducts {
  title: string
  /** The ids the restaurant chose, in the order it chose them. */
  ids: number[]
}

export function pickedProductsOf(module: HomeModule, fallbackTitle: string): PickedProducts {
  const raw = module.content.productIds
  const ids = Array.isArray(raw)
    ? raw.flatMap((entry) => (typeof entry === 'number' && Number.isInteger(entry) ? [entry] : []))
    : []
  return { title: contentText(module, 'title') ?? fallbackTitle, ids }
}

/**
 * The chosen products that still exist, in the chosen order, and how many did not.
 *
 * Measured: `popular_dishes` names 104, 111 and 76; `chef_recommendations` names 94, 7, 8 and 36. An id
 * whose product has been withdrawn is **counted and reported**, not silently skipped — a carousel that
 * quietly shows three of four looks like a carousel of three, and the restaurant has no way to notice
 * that its choice stopped being honoured.
 */
export function resolvePicked<T extends { id: number; active?: boolean }>(
  ids: readonly number[],
  products: readonly T[],
): { found: T[]; missing: number[] } {
  const found: T[] = []
  const missing: number[] = []
  for (const id of ids) {
    const product = products.find((candidate) => candidate.id === id)
    if (product === undefined || product.active === false) missing.push(id)
    else found.push(product)
  }
  return { found, missing }
}

export function missingPickedNote(missing: readonly number[]): string {
  if (missing.length === 0) return ''
  return `${tt('Z tejto sekcie už niektoré položky nie sú v ponuke')}: ${missing.join(', ')}.`
}

// ─── the plain text modules ──────────────────────────────────────────────────

export interface BannerContent {
  hoursText: string | null
  phoneText: string | null
  addressText: string | null
  mobileImageUrl: string | null
  desktopImageUrl: string | null
}

export function bannerOf(module: HomeModule): BannerContent {
  return {
    hoursText: contentText(module, 'hoursText'),
    phoneText: contentText(module, 'phoneText'),
    addressText: contentText(module, 'addressText'),
    mobileImageUrl: contentText(module, 'mobileImageUrl'),
    desktopImageUrl: contentText(module, 'desktopImageUrl'),
  }
}

export interface GuaranteeBlock {
  title: string
  text: string
}

/** The three blocks, minus any left blank — two configured guarantees are two, not three with a gap. */
export function guaranteesOf(module: HomeModule): GuaranteeBlock[] {
  return [1, 2, 3].flatMap((index) => {
    const title = contentText(module, `block${index}Title`)
    const text = contentText(module, `block${index}Text`)
    if (title === null && text === null) return []
    return [{ title: title ?? '', text: text ?? '' }]
  })
}

export interface AboutUs {
  description: string | null
  imageUrl: string | null
  linkUrl: string | null
}

export function aboutUsOf(module: HomeModule): AboutUs {
  return {
    description: contentText(module, 'description'),
    imageUrl: contentText(module, 'imageUrl'),
    // An external link, so only http(s) — a `javascript:` href in an admin-authored field would run
    // in the customer's session, and nothing about this field prevents one being typed.
    linkUrl: safeLink(module.content.linkUrl),
  }
}


export interface Newsletter {
  title: string
  subtitle: string | null
  placeholder: string
  ctaText: string
}

export function newsletterOf(module: HomeModule): Newsletter {
  return {
    title: contentText(module, 'title') ?? tt('Novinky e-mailom'),
    subtitle: contentText(module, 'subtitle'),
    placeholder: contentText(module, 'placeholder') ?? tt('vas@email.sk'),
    ctaText: contentText(module, 'ctaText') ?? tt('Odoberať'),
  }
}

export interface SocialLinks {
  title: string | null
  subtitle: string | null
  /** Out of five. `null` when the field is absent or unreadable, and the section shows legacy's 4.4. */
  googleRating: number | null
}

/**
 * `4,8` and `4.8` and `4.8` typed as a number all mean the same thing.
 *
 * Legacy reads `typeof content.googleRating === "number"` and defaults everything else to 4.4 — so a
 * franchise that typed its rating into the settings box, which is a text input, silently gets somebody
 * else's number. Accepting the comma is the whole difference on a Slovak keyboard.
 */
function ratingOf(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw !== 'string') return null
  const value = Number(raw.trim().replace(',', '.'))
  return Number.isFinite(value) ? value : null
}

export function socialLinksOf(module: HomeModule): SocialLinks {
  return {
    title: contentText(module, 'title'),
    subtitle: contentText(module, 'subtitle'),
    googleRating: ratingOf(module.content.googleRating),
  }
}

export interface Testimonial {
  author: string
  text: string
  rating: number | null
}

export function testimonialsOf(module: HomeModule): Testimonial[] {
  return contentList(module, 'items').flatMap((entry) => {
    const text = typeof entry.text === 'string' ? entry.text.trim() : ''
    if (text === '') return []
    const rating = typeof entry.rating === 'number' ? entry.rating : Number(entry.rating)
    return [
      {
        author: typeof entry.author === 'string' ? entry.author : '',
        text,
        rating: Number.isFinite(rating) && rating > 0 ? rating : null,
      },
    ]
  })
}

/**
 * The customer's own most-ordered products, as `GET /customer-auth/me/most-ordered` answers them.
 *
 * Defensive for the same reason every other reader here is: the row is assembled by a join over the
 * order history and a product that has since been withdrawn comes back with pieces missing. A card with
 * no name is a card nobody can order.
 */
export interface MostOrderedItem {
  id: number
  name: string
  priceCents: number
  imageUrl: string | null
  orderedCount: number
}

export function mostOrderedOf(payload: unknown): MostOrderedItem[] {
  if (!Array.isArray(payload)) return []
  const items: MostOrderedItem[] = []
  for (const raw of payload) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Record<string, unknown>
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    if (typeof row.id !== 'number' || name === '') continue
    const image = typeof row.imageUrl === 'string' ? row.imageUrl.trim() : ''
    items.push({
      id: row.id,
      name,
      priceCents:
        typeof row.price === 'number' || typeof row.price === 'string'
          ? parseMoneyToCents(row.price)
          : 0,
      imageUrl: image === '' ? null : image,
      // Legacy prints the count only when it is above zero, so a zero here means *say nothing* rather
      // than *ordered none times*, which would be an odd thing to tell somebody about their own habit.
      orderedCount: typeof row.orderedCount === 'number' && row.orderedCount > 0 ? row.orderedCount : 0,
    })
  }
  return items
}

// ─── the promo panel, twenty-five fields ─────────────────────────────────────

export interface PromoBanner {
  gradient: string
  offer: {
    shown: boolean
    badgeText: string
    badgeColour: string
    label: string
    labelColour: string
    kicker: string | null
    title: string | null
    highlight: string | null
    subtitle: string | null
    sideBadge: string | null
  }
  loyalty: {
    shown: boolean
    title: string | null
    subtitle: string | null
    tag1: string | null
    tag2: string | null
  }
  pointsCard: { shown: boolean; text: string | null }
  emailHint: { shown: boolean; text: string | null }
  finalNote: string | null
  social: { shown: boolean; rating: number }
}

/** A switch the admin has not touched is on, which is legacy's `bool(v, true)`. */
function contentFlag(module: HomeModule, field: string): boolean {
  const value = module.content[field]
  return typeof value === 'boolean' ? value : true
}

/** A colour the admin has not set falls back to legacy's, which is a design default and safe. */
function contentColour(module: HomeModule, field: string, fallback: string): string {
  return contentText(module, field) ?? fallback
}

/**
 * §9.289 — everything the promo panel draws, read out of its blob.
 *
 * **The copy has no defaults and the styling does**, which is the one place this departs from legacy.
 * `PromoBannerModule.tsx` falls back to `"malá šunková za"` and `"2 €"` because the panel began life
 * hardcoded for this restaurant. Carrying that into a multi-tenant product would put a **price claim on
 * a franchise that never made it** the moment somebody switched the block on. So a missing text field
 * answers `null` and its element is omitted; a missing colour answers legacy's.
 */
export function promoBannerOf(module: HomeModule): PromoBanner {
  const from = contentColour(module, 'gradientFrom', '#7b1f1f')
  const via = contentColour(module, 'gradientVia', '#a82828')
  const to = contentColour(module, 'gradientTo', '#8b2e1a')
  const rating = module.content.googleRating
  return {
    gradient: `linear-gradient(135deg, ${from} 0%, ${via} 45%, ${to} 100%)`,
    offer: {
      shown: contentFlag(module, 'offerEnabled'),
      // `2+1` and `AKCIA` are the disc's design, not an offer: the disc is meaningless empty.
      badgeText: contentText(module, 'offerBadgeText') ?? '2+1',
      badgeColour: contentColour(module, 'offerBadgeBadgeColor', '#c0392b'),
      label: contentText(module, 'offerBadgeLabel') ?? 'AKCIA',
      labelColour: contentColour(module, 'offerBadgeLabelColor', '#e67e22'),
      kicker: contentText(module, 'offerKicker'),
      title: contentText(module, 'offerTitle'),
      highlight: contentText(module, 'offerHighlight'),
      subtitle: contentText(module, 'offerSubtitle'),
      sideBadge: contentText(module, 'offerSideBadge'),
    },
    loyalty: {
      shown: contentFlag(module, 'loyaltyEnabled'),
      title: contentText(module, 'loyaltyTitle'),
      subtitle: contentText(module, 'loyaltySubtitle'),
      tag1: contentText(module, 'loyaltyTag1'),
      tag2: contentText(module, 'loyaltyTag2'),
    },
    pointsCard: {
      shown: contentFlag(module, 'pointsCardEnabled'),
      text: contentText(module, 'pointsCardText'),
    },
    emailHint: {
      shown: contentFlag(module, 'emailHintEnabled'),
      text: contentText(module, 'emailHintText'),
    },
    finalNote: contentText(module, 'finalNote'),
    social: {
      shown: contentFlag(module, 'socialEnabled'),
      // Legacy's own default when the franchise configured none.
      rating: typeof rating === 'number' && rating > 0 ? rating : 4.4,
    },
  }
}
